import InboxOutlined from '@mui/icons-material/InboxOutlined';
import Refresh from '@mui/icons-material/Refresh';
import SearchOffOutlined from '@mui/icons-material/SearchOffOutlined';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import type { Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../../components/AppLayout/AppLayout';
import { useUser } from '../../context/UserContext';
import { s3ObjectPublicUrl } from '../../utils/s3PublicUrl';
import SettingsListDeleteButton from '../Settings/lists/SettingsListDeleteButton';
import {
  ageGroupIntervalLabel,
  authorRowSummaryWithTitle,
  categoryRowSummaryLabel,
  formatSettingsRowDate,
  type AgeGroupRow,
  type AuthorRow,
  type CategoryRow,
} from '../Settings/lists/singleTableListSupport';
import { useAgeGroupList } from '../Settings/lists/useAgeGroupList';
import { useCategoryList } from '../Settings/lists/useCategoryList';
import type { BookListItem } from './bookApi';
import { useBooksList, type BookWithAuthor } from './useBooksList';

const cardSx = {
  position: 'relative' as const,
  height: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
  transition: (theme: Theme) =>
    theme.transitions.create(['box-shadow', 'border-color', 'background-color'], {
      duration: theme.transitions.duration.shortest,
    }),
  borderColor: 'divider',
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: 'action.hover',
    boxShadow: (theme: Theme) => theme.shadows[3],
  },
};

type PublishFilter = 'all' | 'published' | 'unpublished';

function bookDisplayTitle(book: BookListItem): string {
  const { language_map } = book;
  const direct = language_map?.en?.title?.trim();
  if (direct) return direct;
  for (const key of Object.keys(language_map ?? {})) {
    const e = language_map[key];
    if (e?.lang?.toLowerCase() === 'en') {
      return (e.title ?? '').trim();
    }
  }
  for (const key of Object.keys(language_map ?? {})) {
    const t = language_map[key]?.title?.trim();
    if (t) return t;
  }
  return '';
}

function allTitleSearchHaystack(book: BookListItem): string {
  const parts: string[] = [];
  for (const e of Object.values(book.language_map ?? {})) {
    if (e?.title != null) parts.push(String(e.title));
  }
  return parts.join(' ').toLowerCase();
}

function filterBooks(
  books: BookWithAuthor[],
  publishFilter: PublishFilter,
  authorIds: string[],
  categoryIds: string[],
  ageGroupIds: string[],
  titleQuery: string,
): BookWithAuthor[] {
  const q = titleQuery.trim().toLowerCase();
  const authorSet = new Set(authorIds);
  const categoryNums = categoryIds
    .map((id) => parseInt(id, 10))
    .filter((n) => Number.isFinite(n));
  const ageNums = ageGroupIds.map((id) => parseInt(id, 10)).filter((n) => Number.isFinite(n));

  return books.filter((book) => {
    if (publishFilter === 'published' && !book.published) return false;
    if (publishFilter === 'unpublished' && book.published) return false;

    if (authorSet.size > 0 && !authorSet.has(String(book.author_id))) return false;

    if (categoryNums.length > 0) {
      const ok = book.categories.some((c) => categoryNums.includes(c));
      if (!ok) return false;
    }

    if (ageNums.length > 0) {
      const ok = book.age_groups.some((a) => ageNums.includes(a));
      if (!ok) return false;
    }

    if (q && !allTitleSearchHaystack(book).includes(q)) return false;

    return true;
  });
}

export default function BookList() {
  const { rows, authors, loading, error, load, deleteById, deletingId } = useBooksList();
  const { rows: categories, loading: categoriesLoading } = useCategoryList<CategoryRow>();
  const { rows: ageGroups, loading: ageGroupsLoading } = useAgeGroupList<AgeGroupRow>();
  const { user } = useUser();
  const isManager = user?.role === 'MANAGER';

  const [publishFilter, setPublishFilter] = useState<PublishFilter>('all');
  const [selectedAuthors, setSelectedAuthors] = useState<AuthorRow[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<CategoryRow[]>([]);
  const [selectedAgeGroups, setSelectedAgeGroups] = useState<AgeGroupRow[]>([]);
  const [titleQuery, setTitleQuery] = useState('');

  const sortedAuthors = useMemo(() => {
    return [...authors].sort((a, b) =>
      authorRowSummaryWithTitle(a).localeCompare(authorRowSummaryWithTitle(b), 'mn'),
    );
  }, [authors]);

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) =>
      categoryRowSummaryLabel(a).localeCompare(categoryRowSummaryLabel(b), 'mn'),
    );
  }, [categories]);

  const sortedAgeGroupsList = useMemo(() => {
    return [...ageGroups].sort((a, b) =>
      ageGroupIntervalLabel(a).localeCompare(ageGroupIntervalLabel(b), 'mn'),
    );
  }, [ageGroups]);

  const sortedBooks = useMemo(() => {
    return [...rows].sort((a, b) => {
      const ta = new Date(a.updated_at).getTime();
      const tb = new Date(b.updated_at).getTime();
      return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
    });
  }, [rows]);

  const filtered = useMemo(() => {
    return filterBooks(
      sortedBooks,
      publishFilter,
      selectedAuthors.map((a) => a.id),
      selectedCategories.map((c) => c.id),
      selectedAgeGroups.map((g) => g.id),
      titleQuery,
    );
  }, [
    sortedBooks,
    publishFilter,
    selectedAuthors,
    selectedCategories,
    selectedAgeGroups,
    titleQuery,
  ]);

  const hasActiveFilters =
    publishFilter !== 'all' ||
    selectedAuthors.length > 0 ||
    selectedCategories.length > 0 ||
    selectedAgeGroups.length > 0 ||
    titleQuery.trim() !== '';

  const clearFilters = () => {
    setPublishFilter('all');
    setSelectedAuthors([]);
    setSelectedCategories([]);
    setSelectedAgeGroups([]);
    setTitleQuery('');
  };

  const refListsLoading = categoriesLoading || ageGroupsLoading;

  return (
    <AppLayout>
      <Box
        sx={{
          flex: 1,
          py: 4,
          px: { xs: 2, sm: 4 },
          maxWidth: '72rem',
          mx: 'auto',
          width: 1,
        }}
      >
        <Box
          component="header"
          sx={{
            mb: 3,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
            Номын сан
          </Typography>
        </Box>

        <Card variant="outlined" elevation={0} sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              px: 2,
              py: 1.5,
              width: 1,
            }}
          >
            {!loading && rows.length > 0 ? (
              <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ flexShrink: 0 }}>
                Шүүлт
              </Typography>
            ) : null}
            <Box sx={{ flex: 1, minWidth: 0 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {!loading && rows.length > 0 && hasActiveFilters ? (
                <Button size="small" onClick={clearFilters} color="inherit">
                  Шүүлтүүд цэвэрлэх
                </Button>
              ) : null}
              <IconButton
                aria-label="Жагсаалт шинэчлэх"
                onClick={() => void load()}
                disabled={loading}
                color="primary"
              >
                <Refresh />
              </IconButton>
              <Button variant="contained" color="primary" component={Link} to="/books/create">
                Шинэ ном
              </Button>
            </Box>
          </Box>
          <Divider />
          {error && (
            <Alert severity="error" sx={{ borderRadius: 0 }} role="alert">
              {error}
            </Alert>
          )}
          {!loading && rows.length > 0 ? (
            <Box
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Төлөв
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={publishFilter}
                  onChange={(_, v: PublishFilter | null) => {
                    if (v != null) setPublishFilter(v);
                  }}
                  aria-label="Нийтлэлийн төлөвөөр шүүх"
                  sx={{ flexWrap: 'wrap' }}
                >
                  <ToggleButton value="all">Бүгд</ToggleButton>
                  <ToggleButton value="published">Нийтлэгдсэн</ToggleButton>
                  <ToggleButton value="unpublished">Ноорог</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                  gap: 2,
                  alignItems: 'flex-start',
                }}
              >
                <TextField
                  label="Гарчиг хайх"
                  value={titleQuery}
                  onChange={(e) => setTitleQuery(e.target.value)}
                  size="small"
                  fullWidth
                  autoComplete="off"
                  placeholder="Бүх хэлээр"
                />
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={sortedAuthors}
                  getOptionLabel={(o) => authorRowSummaryWithTitle(o)}
                  value={selectedAuthors}
                  onChange={(_, v) => setSelectedAuthors(v)}
                  renderInput={(params) => <TextField {...params} label="Зохиолч" size="small" />}
                  disabled={refListsLoading}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                />
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={sortedCategories}
                  getOptionLabel={(o) => categoryRowSummaryLabel(o)}
                  value={selectedCategories}
                  onChange={(_, v) => setSelectedCategories(v)}
                  renderInput={(params) => <TextField {...params} label="Ангилал" size="small" />}
                  disabled={refListsLoading}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                />
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={sortedAgeGroupsList}
                  getOptionLabel={(o) => ageGroupIntervalLabel(o)}
                  value={selectedAgeGroups}
                  onChange={(_, v) => setSelectedAgeGroups(v)}
                  renderInput={(params) => <TextField {...params} label="Насны ангилал" size="small" />}
                  disabled={refListsLoading}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                />
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Харуулж байна: {filtered.length} / {rows.length}
              </Typography>
            </Box>
          ) : null}

          <Box sx={{ p: 2, pt: 1.5 }}>
            {loading ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))',
                  },
                  gap: 2,
                }}
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
                    <Skeleton variant="rectangular" height={160} animation="wave" />
                    <Box sx={{ p: 2 }}>
                      <Skeleton width="80%" animation="wave" />
                      <Skeleton width="40%" animation="wave" sx={{ mt: 1 }} />
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : rows.length === 0 ? (
              <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <InboxOutlined sx={{ fontSize: 40, color: 'action.disabled' }} aria-hidden />
                <Typography variant="body2" color="text.secondary">
                  Ном одоогоор байхгүй
                </Typography>
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <SearchOffOutlined sx={{ fontSize: 40, color: 'action.disabled' }} aria-hidden />
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Шүүлтэд тохирох ном алга
                </Typography>
                <Button size="small" variant="outlined" onClick={clearFilters}>
                  Шүүлтүүд цэвэрлэх
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
                    lg: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
                  },
                  gap: 2,
                  alignItems: 'stretch',
                }}
              >
                {filtered.map((book) => {
                  const title = bookDisplayTitle(book) || '—';
                  const coverUrl = s3ObjectPublicUrl(book.cover_image_path?.trim() || null);
                  const authorLine = book.author
                    ? authorRowSummaryWithTitle(book.author)
                    : null;
                  const authorForDialog = authorLine ?? `Олдсонгүй (зохиолчийн ID: ${book.author_id})`;
                  return (
                    <Paper
                      key={book.id}
                      variant="outlined"
                      component="article"
                      aria-label={authorLine ? `${title}, ${authorLine}` : title}
                      sx={cardSx}
                    >
                      <Box
                        component={Link}
                        to={`/library/${encodeURIComponent(book.id)}`}
                        sx={{
                          textDecoration: 'none',
                          color: 'inherit',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                          minHeight: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: 1,
                            aspectRatio: '4 / 3',
                            bgcolor: 'action.hover',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          {coverUrl ? (
                            <Box
                              component="img"
                              src={coverUrl}
                              alt=""
                              sx={{
                                width: 1,
                                height: 1,
                                objectFit: 'cover',
                                display: 'block',
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 1,
                                height: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'text.disabled',
                              }}
                            >
                              <Typography variant="caption" fontWeight={600}>
                                Зураггүй
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ minWidth: 0, flex: 1 }}>
                              {title}
                            </Typography>
                            <Chip
                              size="small"
                              label={book.published ? 'Нийтлэгдсэн' : 'Ноорог'}
                              color={book.published ? 'success' : 'default'}
                              variant={book.published ? 'filled' : 'outlined'}
                              sx={{ flexShrink: 0 }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            title={book.id}
                            sx={{
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {book.id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {authorLine ? (
                              <>Зохиолч: {authorLine}</>
                            ) : (
                              <>Зохиолч олдсонгүй (ID: {book.author_id})</>
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 'auto' }}>
                            Шинэчилсэн: {formatSettingsRowDate(book.updated_at)}
                          </Typography>
                        </Box>
                      </Box>
                      {isManager ? (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: (theme) => theme.spacing(0.5),
                            bottom: (theme) => theme.spacing(0.5),
                          }}
                        >
                          <SettingsListDeleteButton
                            id={book.id}
                            deletingId={deletingId}
                            onDelete={deleteById}
                            itemLabel={`Гарчиг: ${title}\nЗохиолч: ${authorForDialog}`}
                          />
                        </Box>
                      ) : null}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        </Card>
      </Box>
    </AppLayout>
  );
}
